import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Copy, Check, Download, Ticket } from 'lucide-react';
import { storeReferralCode } from '../../infrastructure/utils/referralCodeStorage';
import LocalizationService from '../../application/services/LocalizationService';
import './ReferralLandingPage.css';

const t = (key: string) => LocalizationService.translate(`referralLanding.${key}`) as string;

const APP_STORE_URL = 'https://apps.apple.com/app/getspeakhub/id000000000';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.getspeakhub.app';
const REDIRECT_DELAY_MS = 400;
const SHOW_FALLBACK_AFTER_MS = 1800;

/**
 * /i/:code — referral landing (D2, Web UI section). Distinct from the generic OpenInAppPage: this
 * is the ambassador program's public front door, so it earns its own "boarding pass" framing
 * (continuing the passport/ticket world from MyCommunityPage and LeaderboardBoard) rather than a
 * neutral bounce screen, plus the D2-specific job neither existing page does — storing the code
 * (referralCodeStorage) so a visitor without the app yet can recover it after installing.
 */
const ReferralLandingPage: React.FC = () => {
  const { code = '' } = useParams<{ code: string }>();
  const [copied, setCopied] = useState(false);
  const [showFallback, setShowFallback] = useState(false);

  const deepLink = `getspeakhub://i/${code}`;

  useEffect(() => {
    if (!code) {
      return;
    }
    storeReferralCode(code);

    const openTimer = setTimeout(() => {
      window.location.href = deepLink;
    }, REDIRECT_DELAY_MS);
    const fallbackTimer = setTimeout(() => setShowFallback(true), SHOW_FALLBACK_AFTER_MS);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(fallbackTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const handleCopy = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!code) {
    return (
      <div className="rl-root">
        <div className="rl-ticket">
          <p className="rl-missing">{t('missingCode')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="rl-root">
      <div className="rl-ticket">
        <div className="rl-ticket-top">
          <span className="rl-eyebrow">{t('eyebrow')}</span>
          <h1 className="rl-headline">{t('headline')}</h1>
          <p className="rl-sub">{t('sub')}</p>
        </div>

        <div className="rl-perforation" role="presentation" />

        <div className="rl-ticket-bottom">
          <span className="rl-code-label">{t('codeLabel')}</span>
          <div className="rl-code-row">
            <Ticket size={20} className="rl-code-icon" />
            <span className="rl-code">{code}</span>
          </div>

          <button className="rl-primary-btn" onClick={() => { window.location.href = deepLink; }}>
            {t('openInApp')}
          </button>

          {showFallback && (
            <>
              <p className="rl-fallback-hint">{t('noAppYet')}</p>
              <div className="rl-store-row">
                <a href={APP_STORE_URL} target="_blank" rel="noreferrer" className="rl-store-btn">
                  <Download size={14} /> {t('appStore')}
                </a>
                <a href={PLAY_STORE_URL} target="_blank" rel="noreferrer" className="rl-store-btn">
                  <Download size={14} /> {t('googlePlay')}
                </a>
              </div>
              <button className="rl-copy-btn" onClick={handleCopy}>
                {copied ? <Check size={14} /> : <Copy size={14} />}
                {copied ? t('copied') : t('copyCode')}
              </button>
            </>
          )}
        </div>
      </div>

      <p className="rl-footer">
        <a href="/terms">{LocalizationService.translate('terms') as string}</a> &middot; <a href="/privacy">{LocalizationService.translate('privacy') as string}</a>
      </p>
    </div>
  );
};

export default ReferralLandingPage;
